<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import {
    ArrowLeft,
    ChevronDown,
    ClipboardCheck,
    ClipboardList,
    ClipboardPlus,
    ClipboardX,
    Files,
    Loader2
  } from 'lucide-vue-next'
  import { Button } from '@/components/ui/button'
  import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
  import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
  } from '@/components/ui/table'
  import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger
  } from '@/components/ui/alert-dialog'
  import SpaCompatComponent from '@/components/app/SpaCompatComponent.vue'
  import SortableTableHeader from '@/components/admin/SortableTableHeader.vue'
  import PaginatedTableView, {
    type FetchDataOptions,
    type PaginatedResponse
  } from '@/components/admin/PaginatedTable.vue'
  import SkeletonLoader from '@/components/skeleton/SkeletonLoader.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { useMetrics, MetricEventType } from '@/composables/useMetrics'
  import { DuplicateCaseMatchesService, CasesService } from '@/types/generated/services.gen'
  import type { DuplicateCaseMatch, CustomerCase } from '@/types/generated/types.gen'
  import { EntityTypeEnum } from '@/types/generated/types.gen'
  import { useToast } from '@/components/ui/toast/use-toast'
  import ChatEmptyStateIllustration from '@/components/svg/Illustrations/ChatEmptyStateIllustration.vue'
  import { formatDateGerman } from '@/lib/util/formatting'

  const props = defineProps<{
    mode?: 'suggestions' | 'manual'
  }>()
  const mode = computed(() => props.mode ?? 'suggestions')

  const router = useRouter()
  const route = useRoute()
  const { toast } = useToast()
  const { trackEvent } = useMetrics()

  const searchQuery = ref('')
  const stateFilter = ref<string>('all')
  const tableRef = ref<InstanceType<typeof PaginatedTableView> | null>(null)

  const stateCards = [
    { value: 'all', labelKey: 'stateAll', icon: Files },
    { value: 'NEW', labelKey: 'stateNew', icon: ClipboardPlus },
    { value: 'IN_PROGRESS', labelKey: 'stateInProgress', icon: ClipboardList },
    { value: 'COMPLETED', labelKey: 'stateCompleted', icon: ClipboardCheck },
    { value: 'REJECTED', labelKey: 'stateRejected', icon: ClipboardX }
  ] as const

  const setStateFilter = (value: string) => {
    stateFilter.value = value
    // An "all matching" selection is scoped to its filter set; once filters
    // change the prior UUIDs no longer match what the user sees.
    if (allMatchingChosen.value) {
      selectedCaseUuids.value = new Set()
    }
    allMatchingChosen.value = false
    tableRef.value?.resetAndRefresh()
  }

  const { t } = useTranslation({
    en: {
      back: 'Back to Case Overview',
      backManual: 'Back',
      newCaseDescription:
        'This is the newly created case. It will be merged into an existing case.',
      selectCaseToRemain: 'Select the case that should remain after the merge.',
      clientName: 'Client Name',
      opponentName: 'Opponent Name',
      name: 'Name',
      received: 'Received',
      lawFieldTopic: 'Law field / Topic',
      note: 'Note',
      cancel: 'Cancel',
      mergeDuplicateCases: 'Merge Duplicate Cases',
      mergeAndUpdate: 'Merge',
      mergeCases: 'Merge cases',
      mergeDialogTitle: 'Merge requests and remove duplicates',
      mergeDialogDescription:
        'After merging, duplicate requests will be permanently deleted and cannot be restored. All selected information will be retained.',
      mergeDialogNote:
        'After merging, go to the existing requests to confirm the update of protected fields such as phone numbers and email addresses.',
      caseSummary: 'Case summary',
      clientInformation: 'Client Information',
      opponentInformation: 'Opponent Information',
      salutation: 'Salutation:',
      firstName: 'First name:',
      lastName: 'Last name:',
      dateOfBirth: 'Date of birth:',
      phone: 'Phone:',
      email: 'Email:',
      street: 'Street:',
      streetNr: 'Street No.:',
      postalCode: 'Postal code:',
      city: 'City:',
      companyName: 'Company name:',
      legalForm: 'Legal form:',
      institutionName: 'Institution name:',
      contactFirstName: 'Contact first name:',
      contactLastName: 'Contact last name:',
      contactDateOfBirth: 'Contact date of birth:',
      ai: 'AI',
      loadingDuplicateCases: 'Loading duplicate cases...',
      noDuplicateCasesFound: 'No duplicate cases found.',
      backToCase: 'Back to Case',
      retry: 'Retry',
      error: 'Error',
      failedToLoadDuplicateCases: 'Failed to load requests.',
      manualMergeTitle: 'Merge requests',
      manualSourceDescription: 'The selected requests will be merged into this request.',
      manualSelectTarget: 'Pick the requests to merge in.',
      searchPlaceholder: 'Search by client, opponent, note...',
      stateAll: 'All',
      stateNew: 'New',
      stateInProgress: 'In Progress',
      stateCompleted: 'Completed',
      stateRejected: 'Rejected',
      noCasesFound: 'No requests match your search.',
      manualMergeDialogTitle: 'Merge requests',
      manualMergeDialogDescription:
        'The selected requests will be merged into this request and deleted. Cannot be undone.',
      manualMergeCta: 'Merge',
      targetBadge: 'Target',
      changeTargetCta: 'Set as target',
      selectAllMatching: 'Select all {count} matching requests',
      clearSelection: 'Clear selection',
      allMatchingSelected: 'All {count} matching requests are selected.',
      mergingTitle: 'Merging requests…',
      mergingDescription: 'This can take a moment. Please don’t close this tab.'
    },
    de: {
      back: 'Zurück zur Fallübersicht',
      backManual: 'Zurück',
      newCaseDescription:
        'Dies ist der neu erstellte Fall. Er wird in einen bestehenden Fall zusammengeführt.',
      selectCaseToRemain:
        'Wählen Sie den Fall aus, der nach der Zusammenführung bestehen bleiben soll.',
      clientName: 'Mandantenname',
      opponentName: 'Gegnername',
      name: 'Name',
      received: 'Eingegangen',
      lawFieldTopic: 'Rechtsgebiet / Thema',
      note: 'Notiz',
      cancel: 'Abbrechen',
      mergeDuplicateCases: 'Duplikat-Anfragen zusammenführen',
      mergeAndUpdate: 'Zusammenführen',
      mergeCases: 'Anfragen zusammenführen',
      mergeDialogTitle: 'Anfragen zusammenführen und Duplikate entfernen',
      mergeDialogDescription:
        'Nach dem Zusammenführen werden doppelte Anfragen dauerhaft gelöscht und können nicht wiederhergestellt werden. Alle ausgewählten Informationen bleiben erhalten.',
      mergeDialogNote:
        'Gehen Sie nach dem Zusammenführen zu den bestehenden Anfragen, um die Aktualisierung geschützter Felder wie Telefonnummern und E-Mail-Adressen zu bestätigen.',
      caseSummary: 'Fallzusammenfassung',
      clientInformation: 'Mandanteninformationen',
      opponentInformation: 'Gegnerinformationen',
      salutation: 'Anrede:',
      firstName: 'Vorname:',
      lastName: 'Nachname:',
      dateOfBirth: 'Geburtsdatum:',
      phone: 'Telefon:',
      email: 'E-Mail:',
      street: 'Straße:',
      streetNr: 'Hausnummer:',
      postalCode: 'Postleitzahl:',
      city: 'Stadt:',
      companyName: 'Firmenname:',
      legalForm: 'Rechtsform:',
      institutionName: 'Institutionsname:',
      contactFirstName: 'Kontakt Vorname:',
      contactLastName: 'Kontakt Nachname:',
      contactDateOfBirth: 'Kontakt Geburtsdatum:',
      ai: 'KI',
      loadingDuplicateCases: 'Duplikat-Anfragen werden geladen...',
      noDuplicateCasesFound: 'Keine Duplikat-Anfragen gefunden.',
      backToCase: 'Zurück zum Fall',
      retry: 'Erneut versuchen',
      error: 'Fehler',
      failedToLoadDuplicateCases: 'Fehler beim Laden der Anfragen.',
      manualMergeTitle: 'Anfragen zusammenführen',
      manualSourceDescription: 'Die ausgewählten Anfragen werden in diese Anfrage zusammengeführt.',
      manualSelectTarget: 'Wählen Sie die zusammenzuführenden Anfragen aus.',
      searchPlaceholder: 'Suche nach Mandant, Gegner, Notiz...',
      stateAll: 'Alle',
      stateNew: 'Neu',
      stateInProgress: 'In Bearbeitung',
      stateCompleted: 'Abgeschlossen',
      stateRejected: 'Abgelehnt',
      noCasesFound: 'Keine Anfragen passen zur Suche.',
      manualMergeDialogTitle: 'Anfragen zusammenführen',
      manualMergeDialogDescription:
        'Die ausgewählten Anfragen werden in diese Anfrage zusammengeführt und gelöscht. Nicht umkehrbar.',
      manualMergeCta: 'Zusammenführen',
      targetBadge: 'Ziel',
      changeTargetCta: 'Als Ziel festlegen',
      selectAllMatching: 'Alle {count} passenden Anfragen auswählen',
      clearSelection: 'Auswahl aufheben',
      allMatchingSelected: 'Alle {count} passenden Anfragen sind ausgewählt.',
      mergingTitle: 'Anfragen werden zusammengeführt…',
      mergingDescription: 'Das kann einen Moment dauern. Bitte schließen Sie diesen Tab nicht.'
    },
    fr: {
      back: 'Retour à la vue des dossiers',
      backManual: 'Retour',
      newCaseDescription:
        'Voici la demande nouvellement créée. Elle sera fusionnée dans une demande existante.',
      selectCaseToRemain: 'Sélectionnez la demande à conserver après la fusion.',
      clientName: 'Nom du client',
      opponentName: "Nom de l'adversaire",
      name: 'Nom',
      received: 'Reçu',
      lawFieldTopic: 'Domaine / Sujet',
      note: 'Note',
      cancel: 'Annuler',
      mergeDuplicateCases: 'Fusionner les demandes en double',
      mergeAndUpdate: 'Fusionner',
      mergeCases: 'Fusionner les demandes',
      mergeDialogTitle: 'Fusionner les demandes et supprimer les doublons',
      mergeDialogDescription:
        'Après la fusion, les demandes en double seront définitivement supprimées et ne pourront pas être restaurées. Toutes les informations sélectionnées seront conservées.',
      mergeDialogNote:
        'Après la fusion, allez dans les demandes existantes pour confirmer la mise à jour des champs protégés tels que les numéros de téléphone et les adresses e-mail.',
      caseSummary: 'Résumé de la demande',
      clientInformation: 'Informations sur le client',
      opponentInformation: "Informations sur l'adversaire",
      salutation: 'Civilité :',
      firstName: 'Prénom :',
      lastName: 'Nom :',
      dateOfBirth: 'Date de naissance :',
      phone: 'Téléphone :',
      email: 'E-mail :',
      street: 'Rue :',
      streetNr: 'Numéro :',
      postalCode: 'Code postal :',
      city: 'Ville :',
      companyName: "Nom de l'entreprise :",
      legalForm: 'Forme juridique :',
      institutionName: "Nom de l'institution :",
      contactFirstName: 'Prénom du contact :',
      contactLastName: 'Nom du contact :',
      contactDateOfBirth: 'Date de naissance du contact :',
      ai: 'IA',
      loadingDuplicateCases: 'Chargement des demandes en double...',
      noDuplicateCasesFound: 'Aucune demande en double trouvée.',
      backToCase: 'Retour à la demande',
      retry: 'Réessayer',
      error: 'Erreur',
      failedToLoadDuplicateCases: 'Échec du chargement des demandes.',
      manualMergeTitle: 'Fusionner les demandes',
      manualSourceDescription: 'Les demandes sélectionnées seront fusionnées dans cette demande.',
      manualSelectTarget: 'Choisissez les demandes à fusionner.',
      searchPlaceholder: 'Rechercher par client, adversaire, note...',
      stateAll: 'Toutes',
      stateNew: 'Nouvelles',
      stateInProgress: 'En cours',
      stateCompleted: 'Terminées',
      stateRejected: 'Refusées',
      noCasesFound: 'Aucune demande ne correspond à votre recherche.',
      manualMergeDialogTitle: 'Fusionner les demandes',
      manualMergeDialogDescription:
        'Les demandes sélectionnées seront fusionnées dans cette demande et supprimées. Action irréversible.',
      manualMergeCta: 'Fusionner',
      targetBadge: 'Cible',
      changeTargetCta: 'Définir comme cible',
      selectAllMatching: 'Sélectionner les {count} demandes correspondantes',
      clearSelection: 'Effacer la sélection',
      allMatchingSelected: 'Les {count} demandes correspondantes sont sélectionnées.',
      mergingTitle: 'Fusion des demandes en cours…',
      mergingDescription: 'Cela peut prendre un instant. Veuillez ne pas fermer cet onglet.'
    }
  })

  const selectedCaseUuid = ref<string>('')
  const selectedCaseUuids = ref<Set<string>>(new Set())
  const expandedRowUuid = ref<string | null>(null)
  const sortBy = ref<string>('-received_date')

  const sourceCaseUuid = ref<string>('')
  const targetCaseUuid = ref<string>('')
  const isMerging = ref(false)
  const isButtonDisabled = computed(() =>
    mode.value === 'manual' ? selectedCaseUuids.value.size === 0 : !selectedCaseUuid.value
  )

  const isCheckboxRowSelected = (uuid: string) => selectedCaseUuids.value.has(uuid)
  const toggleCheckboxRow = (uuid: string) => {
    const next = new Set(selectedCaseUuids.value)
    if (next.has(uuid)) {
      next.delete(uuid)
    } else {
      next.add(uuid)
    }
    selectedCaseUuids.value = next
    allMatchingChosen.value = false
  }

  const currentPageItems = ref<DuplicateCaseMatch[]>([])
  const totalMatching = ref<number>(0)
  const allMatchingChosen = ref(false)

  const selectableOnPage = computed(() =>
    currentPageItems.value
      .map((it) => it.target_case_uuid)
      .filter((uuid) => uuid && uuid !== targetCaseUuid.value)
  )

  const pageSelectionState = computed<'none' | 'some' | 'all'>(() => {
    const ids = selectableOnPage.value
    if (ids.length === 0) return 'none'
    const selectedOnPage = ids.filter((uuid) => selectedCaseUuids.value.has(uuid)).length
    if (selectedOnPage === 0) return 'none'
    if (selectedOnPage === ids.length) return 'all'
    return 'some'
  })

  const togglePageSelection = () => {
    const next = new Set(selectedCaseUuids.value)
    if (pageSelectionState.value === 'all') {
      for (const uuid of selectableOnPage.value) next.delete(uuid)
    } else {
      for (const uuid of selectableOnPage.value) next.add(uuid)
    }
    selectedCaseUuids.value = next
    allMatchingChosen.value = false
  }

  const selectAllMatching = async () => {
    // Org-scoped case counts sit well below five figures, so one large page
    // is simpler than paginating UUID collection.
    const search = searchQuery.value.trim()
    try {
      const data = await CasesService.casesList({
        limit: 10000,
        search: search || undefined,
        state: stateFilter.value !== 'all' ? stateFilter.value : undefined
      })
      const next = new Set(selectedCaseUuids.value)
      for (const c of data.results ?? []) {
        const uuid = (c as { uuid?: string }).uuid
        if (uuid && uuid !== targetCaseUuid.value) next.add(uuid)
      }
      selectedCaseUuids.value = next
      allMatchingChosen.value = true
    } catch {
      toast({
        title: t('error'),
        description: t('failedToLoadDuplicateCases'),
        variant: 'destructive'
      })
    }
  }

  const clearSelection = () => {
    selectedCaseUuids.value = new Set()
    allMatchingChosen.value = false
  }

  const setTargetCase = async (newTargetUuid: string) => {
    if (newTargetUuid === targetCaseUuid.value) return
    // Fetch first so the header row can't end up out-of-sync with the badge
    // and submission UUID if the request fails.
    let newDetails: CustomerCase
    try {
      newDetails = (await CasesService.casesRetrieve({
        uuid: newTargetUuid
      })) as CustomerCase
    } catch (err) {
      console.error('Error fetching target case:', err)
      toast({
        title: t('error'),
        description: t('failedToLoadDuplicateCases'),
        variant: 'destructive'
      })
      return
    }
    const previousTarget = targetCaseUuid.value
    // allMatchingChosen excludes the previous target; that exclusion no longer
    // makes sense under a new target, so reset and let the user re-pick.
    const next = allMatchingChosen.value ? new Set<string>() : new Set(selectedCaseUuids.value)
    next.delete(newTargetUuid)
    if (!allMatchingChosen.value && previousTarget) next.add(previousTarget)
    selectedCaseUuids.value = next
    targetCaseUuid.value = newTargetUuid
    targetCaseDetails.value = newDetails
    allMatchingChosen.value = false
  }
  const isLoading = ref(true)
  const fetchError = ref(false)

  const sourceCase = ref<CustomerCase | null>(null)
  const duplicateCases = ref<DuplicateCaseMatch[]>([])
  const targetCaseDetails = ref<CustomerCase | null>(null)
  const targetCase = computed(() =>
    mode.value === 'manual' ? targetCaseDetails.value : sourceCase.value
  )
  const targetCaseClient = computed(() => targetCase.value?.data?.client)
  const targetCaseOpponent = computed(() => targetCase.value?.data?.opponent)

  const sourceCaseClient = computed(() => sourceCase.value?.data?.client)
  const sourceCaseOpponent = computed(() => sourceCase.value?.data?.opponent)

  type Entity = DuplicateCaseMatch['target_case_client']

  const formatEntityName = (entity: Entity | undefined) => {
    if (!entity) return 'N/A'

    const firstName = entity.first_name?.trim()
    const lastName = entity.last_name?.trim()

    const getPersonalName = () => {
      return [firstName, lastName].filter(Boolean).join(' ') || null
    }

    switch (entity.entity_type) {
      case EntityTypeEnum.NATURAL_PERSON: {
        return getPersonalName() || 'N/A'
      }

      case EntityTypeEnum.LEGAL_PERSON: {
        const companyName = entity.company_name?.trim()
        return companyName || getPersonalName() || 'N/A'
      }

      case EntityTypeEnum.INSTITUTION: {
        const institutionName = entity.institution_name?.trim()
        return institutionName || getPersonalName() || 'N/A'
      }

      default:
        return 'N/A'
    }
  }

  const formatDate = (dateStr: string | undefined | null, fallback: string = 'N/A') => {
    if (!dateStr) return fallback
    const parsed = new Date(dateStr)
    if (Number.isNaN(parsed.getTime())) return fallback
    return formatDateGerman(dateStr)
  }

  const formatLawFieldTopic = (field: string | undefined, topic: string | undefined) => {
    if (!field && !topic) return ''
    if (!field) return topic || ''
    if (!topic) return field
    return `${field} / ${topic}`
  }

  const sortedCases = computed(() => {
    const cases = [...duplicateCases.value]
    const field = sortBy.value.replace('-', '')
    const isDesc = sortBy.value.startsWith('-')

    if (field === 'received_date') {
      cases.sort((a, b) => {
        const aTime = new Date(a.target_case_created_at).getTime()
        const bTime = new Date(b.target_case_created_at).getTime()

        if (aTime < bTime) return isDesc ? 1 : -1
        if (aTime > bTime) return isDesc ? -1 : 1
        return 0
      })
    }

    return cases
  })

  const handleSort = (field: string, direction: 'asc' | 'desc') => {
    sortBy.value = direction === 'desc' ? `-${field}` : field
  }

  const handleMerge = async () => {
    try {
      isMerging.value = true

      if (mode.value === 'manual') {
        if (selectedCaseUuids.value.size === 0) return
        const response =
          await DuplicateCaseMatchesService.duplicateCaseMatchesForCaseManualMergeCreate({
            caseUuid: targetCaseUuid.value,
            requestBody: { source_case_uuids: Array.from(selectedCaseUuids.value) }
          })
        // replace() so the manual-merge URL doesn't enter history — Back from
        // the target's case detail returns to the original entry point.
        window.location.replace(`/app/cases/legacy/${response.target_case_uuid}`)
        return
      }

      if (!selectedCaseUuid.value) return
      const response = await DuplicateCaseMatchesService.duplicateCaseMatchesForCaseMergeCreate({
        caseUuid: sourceCaseUuid.value,
        requestBody: { target_case_uuid: selectedCaseUuid.value }
      })
      window.location.href = `/app/cases/legacy/${response.target_case_uuid}`
    } catch (err: any) {
      console.error('Error merging cases:', err)
      toast({
        title: t('error'),
        description: err?.body?.detail || err?.message || 'Failed to merge cases.',
        variant: 'destructive'
      })
      // Release the modal only on failure — on success the redirect tears the page down.
      isMerging.value = false
    }
  }

  const handleBack = () => {
    // Prefer the actual entry point (kebab on case list vs. case detail). Fall
    // back to the source case detail when the tab has no prior page (direct
    // nav, bookmark, refresh).
    if (window.history.length > 1) {
      router.back()
    } else {
      router.push(`/app/cases/legacy/${sourceCaseUuid.value}`)
    }
  }

  const handleCancel = () => {
    handleBack()
  }

  const isExpanded = (caseUuid: string) => {
    return expandedRowUuid.value === caseUuid
  }

  const expandedCaseDetails = ref<Record<string, CustomerCase>>({})

  const toggleExpanded = (caseUuid: string) => {
    if (isExpanded(caseUuid)) {
      expandedRowUuid.value = null
    } else {
      expandedRowUuid.value = caseUuid
      trackEvent(MetricEventType.DUPLICATE_CASE_BANNER_SUGGESTION_EXPANDED, {
        case_uuid: caseUuid
      })
      if (mode.value === 'manual' && !expandedCaseDetails.value[caseUuid]) {
        CasesService.casesRetrieve({ uuid: caseUuid })
          .then((c) => {
            expandedCaseDetails.value = {
              ...expandedCaseDetails.value,
              [caseUuid]: c as CustomerCase
            }
          })
          .catch((err) => console.error('Error fetching case details:', err))
      }
    }
  }

  const getInfoFieldsRows = (entity: Entity | undefined) => {
    if (!entity) return []

    if (entity.entity_type === EntityTypeEnum.LEGAL_PERSON) {
      return [
        [
          { label: t('companyName'), value: entity.company_name || 'N/A' },
          { label: t('legalForm'), value: entity.company_legal_form || 'N/A' }
        ],
        [
          { label: t('contactFirstName'), value: entity.first_name || 'N/A' },
          { label: t('contactLastName'), value: entity.last_name || 'N/A' }
        ],
        [
          { label: t('contactDateOfBirth'), value: formatDate(entity.date_of_birth) },
          { label: t('phone'), value: entity.phone || 'N/A' }
        ],
        [{ label: t('email'), value: entity.email || 'N/A' }]
      ]
    }

    if (entity.entity_type === EntityTypeEnum.INSTITUTION) {
      return [
        [{ label: t('institutionName'), value: entity.institution_name || 'N/A' }],
        [
          { label: t('contactFirstName'), value: entity.first_name || 'N/A' },
          { label: t('contactLastName'), value: entity.last_name || 'N/A' }
        ],
        [
          { label: t('contactDateOfBirth'), value: formatDate(entity.date_of_birth) },
          { label: t('phone'), value: entity.phone || 'N/A' }
        ],
        [{ label: t('email'), value: entity.email || 'N/A' }]
      ]
    }

    return [
      [{ label: t('salutation'), value: 'N/A' }],
      [
        { label: t('firstName'), value: entity.first_name || 'N/A' },
        { label: t('lastName'), value: entity.last_name || 'N/A' }
      ],
      [
        { label: t('dateOfBirth'), value: formatDate(entity.date_of_birth) },
        { label: t('phone'), value: entity.phone || 'N/A' }
      ],
      [{ label: t('email'), value: entity.email || 'N/A' }]
    ]
  }

  const caseToMatchShape = (c: CustomerCase): DuplicateCaseMatch =>
    ({
      uuid: c.uuid,
      target_case_uuid: c.uuid,
      target_case_client: c.data?.client,
      target_case_opponent: c.data?.opponent,
      target_case_law_field: c.data?.law?.field ?? '',
      target_case_law_topic: c.data?.law?.topic ?? '',
      target_case_created_at: c.created_at,
      target_case_note: c.note ?? '',
      case_title: c.case_title ?? '',
      reasoning: ''
    }) as unknown as DuplicateCaseMatch

  const fetchDuplicateCases = async () => {
    try {
      isLoading.value = true
      fetchError.value = false

      if (mode.value === 'manual') {
        sourceCase.value = (await CasesService.casesRetrieve({
          uuid: sourceCaseUuid.value
        })) as CustomerCase
        targetCaseDetails.value = sourceCase.value
      } else {
        const [source, matchesResponse] = (await Promise.all([
          CasesService.casesRetrieve({ uuid: sourceCaseUuid.value }),
          DuplicateCaseMatchesService.duplicateCaseMatchesForCaseList({
            caseUuid: sourceCaseUuid.value
          })
        ])) as [CustomerCase, DuplicateCaseMatch[]]

        sourceCase.value = source
        duplicateCases.value = matchesResponse
      }
    } catch (err: any) {
      console.error('Error fetching duplicate cases:', err)
      fetchError.value = true
      duplicateCases.value = []
      toast({
        title: t('error'),
        description: t('failedToLoadDuplicateCases'),
        variant: 'destructive'
      })
    } finally {
      isLoading.value = false
    }
  }

  const fetchManualCandidates = async (
    options?: FetchDataOptions
  ): Promise<PaginatedResponse<DuplicateCaseMatch>> => {
    const search = searchQuery.value.trim()
    const response = await CasesService.casesList({
      limit: options?.limit ?? undefined,
      offset: options?.offset ?? undefined,
      search: search || undefined,
      state: stateFilter.value !== 'all' ? stateFilter.value : undefined
    })

    const sourceResults = (response.results ?? []) as unknown as CustomerCase[]
    const results = sourceResults.map(caseToMatchShape)
    totalMatching.value = response.count ?? results.length
    currentPageItems.value = results
    return {
      ...(response as unknown as PaginatedResponse<CustomerCase>),
      results
    }
  }

  let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null
  const onFilterChange = () => {
    if (mode.value !== 'manual') return
    if (allMatchingChosen.value) {
      selectedCaseUuids.value = new Set()
    }
    allMatchingChosen.value = false
    if (searchDebounceTimer) clearTimeout(searchDebounceTimer)
    searchDebounceTimer = setTimeout(() => {
      tableRef.value?.resetAndRefresh()
    }, 250)
  }

  onMounted(async () => {
    sourceCaseUuid.value = route.params.uuid as string
    targetCaseUuid.value = sourceCaseUuid.value
    await fetchDuplicateCases()
  })
</script>

<template>
  <SpaCompatComponent>
    <div class="min-h-screen bg-gray-50">
      <div class="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <button
          class="mb-4 inline-flex items-center gap-2 text-sm text-neutral-500 underline hover:text-neutral-900"
          @click="handleBack"
        >
          <ArrowLeft class="h-5 w-5" />
          {{ mode === 'manual' ? t('backManual') : t('back') }}
        </button>
        <h1 class="mb-6 text-xl font-semibold text-neutral-900">
          {{ mode === 'manual' ? t('manualMergeTitle') : t('mergeDuplicateCases') }}
        </h1>

        <!-- Loading State -->
        <div
          v-if="isLoading"
          class="overflow-hidden rounded-lg border border-neutral-200 bg-white p-4 shadow-sm"
        >
          <SkeletonLoader
            class="mb-4"
            width="420px"
            height="16px"
          />

          <div class="mb-6 overflow-hidden rounded-lg border border-neutral-200">
            <div class="flex gap-4 border-b border-neutral-200 bg-neutral-50/50 px-4 py-3">
              <SkeletonLoader
                v-for="i in 5"
                :key="i"
                bg-class="bg-gray-200"
                height="14px"
              />
            </div>
            <div class="flex items-center gap-4 px-4 py-4">
              <SkeletonLoader
                v-for="i in 5"
                :key="i"
                height="14px"
              />
            </div>
          </div>

          <SkeletonLoader
            v-if="mode === 'manual'"
            class="mb-4"
            width="320px"
            height="16px"
          />

          <div
            v-if="mode === 'manual'"
            class="mb-4 grid grid-cols-2 gap-2 md:gap-3 lg:grid-cols-5 lg:gap-4"
          >
            <SkeletonLoader
              v-for="i in 5"
              :key="i"
              height="48px"
              css-class="rounded-lg"
            />
          </div>

          <SkeletonLoader
            v-if="mode === 'manual'"
            class="mb-4"
            height="40px"
            css-class="rounded-md"
          />

          <div class="overflow-hidden rounded-lg border border-neutral-200">
            <div
              class="flex items-center gap-4 border-b border-neutral-200 bg-neutral-50/50 px-4 py-3"
            >
              <SkeletonLoader
                width="20px"
                height="20px"
              />
              <SkeletonLoader
                v-for="i in 5"
                :key="i"
                bg-class="bg-gray-200"
                height="14px"
              />
            </div>
            <div
              v-for="i in 6"
              :key="i"
              class="flex items-center gap-4 border-b border-neutral-100 px-4 py-4 last:border-b-0"
            >
              <SkeletonLoader
                width="20px"
                height="20px"
              />
              <SkeletonLoader
                v-for="j in 5"
                :key="j"
                height="14px"
              />
            </div>
          </div>
        </div>

        <!-- Error State -->
        <div
          v-else-if="fetchError"
          class="overflow-hidden rounded-lg border border-neutral-200 bg-white p-8 text-center shadow-sm"
        >
          <p class="text-neutral-600">{{ t('failedToLoadDuplicateCases') }}</p>
          <Button
            class="mt-4"
            @click="fetchDuplicateCases"
          >
            {{ t('retry') }}
          </Button>
        </div>

        <!-- No Duplicates Found (suggestions mode only) -->
        <div
          v-else-if="mode === 'suggestions' && duplicateCases.length === 0"
          class="overflow-hidden rounded-lg border border-neutral-200 bg-white p-8 text-center shadow-sm"
        >
          <p class="text-neutral-600">{{ t('noDuplicateCasesFound') }}</p>
          <ChatEmptyStateIllustration class="h-60 w-60" />
          <Button
            class="mt-4"
            @click="handleBack"
          >
            {{ t('backToCase') }}
          </Button>
        </div>

        <!-- Main Content -->
        <div
          v-else-if="sourceCase"
          class="overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-sm"
        >
          <div class="p-4">
            <!-- Source Case Section -->
            <p class="mb-4 text-base text-neutral-700">
              {{ mode === 'manual' ? t('manualSourceDescription') : t('newCaseDescription') }}
            </p>
            <Table>
              <TableHeader>
                <TableRow class="border-b border-neutral-200 bg-neutral-50/50">
                  <TableHead
                    v-if="mode === 'manual'"
                    class="whitespace-nowrap text-sm font-semibold text-neutral-800"
                  >
                    {{ t('name') }}
                  </TableHead>
                  <template v-else>
                    <TableHead class="whitespace-nowrap text-sm font-semibold text-neutral-800">
                      {{ t('clientName') }}
                    </TableHead>
                    <TableHead class="whitespace-nowrap text-sm font-semibold text-neutral-800">
                      {{ t('opponentName') }}
                    </TableHead>
                  </template>
                  <TableHead class="whitespace-nowrap text-sm font-semibold text-neutral-800">
                    {{ t('received') }}
                  </TableHead>
                  <TableHead class="whitespace-nowrap text-sm font-semibold text-neutral-800">
                    {{ t('lawFieldTopic') }}
                  </TableHead>
                  <TableHead class="whitespace-nowrap text-sm font-semibold text-neutral-800">
                    {{ t('note') }}
                  </TableHead>
                  <TableHead class="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow
                  class="border-b border-neutral-100 hover:bg-neutral-50/50"
                  :class="{ 'bg-gray-50 hover:bg-gray-50': isExpanded(targetCase?.uuid || '') }"
                >
                  <TableCell
                    v-if="mode === 'manual'"
                    class="py-3 text-sm font-medium text-neutral-900"
                  >
                    <div class="flex items-center gap-2">
                      <span
                        class="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700"
                      >
                        {{ t('targetBadge') }}
                      </span>
                      {{ targetCase?.case_title || '-' }}
                    </div>
                  </TableCell>
                  <template v-else>
                    <TableCell class="py-3 text-sm font-medium text-neutral-900">
                      {{ formatEntityName(sourceCaseClient) }}
                    </TableCell>
                    <TableCell class="py-3 text-sm text-neutral-700">
                      {{ formatEntityName(sourceCaseOpponent) }}
                    </TableCell>
                  </template>
                  <TableCell class="whitespace-nowrap py-3 text-sm text-neutral-700">
                    {{ formatDate(targetCase?.created_at) }}
                  </TableCell>
                  <TableCell class="py-1.5 text-sm">
                    <div class="text-neutral-800">
                      {{
                        formatLawFieldTopic(
                          targetCase?.data?.law?.field,
                          targetCase?.data?.law?.topic
                        )
                      }}
                    </div>
                  </TableCell>
                  <TableCell class="max-w-[200px] py-2">
                    <div class="line-clamp-2 text-sm text-neutral-800">
                      {{ targetCase?.note || '' }}
                    </div>
                  </TableCell>
                  <TableCell class="w-[50px]">
                    <button
                      class="flex h-full w-full items-center justify-center text-neutral-500 hover:text-neutral-900"
                      @click="toggleExpanded(targetCase?.uuid || '')"
                    >
                      <ChevronDown
                        class="h-6 w-6 transition-transform"
                        :class="{ 'rotate-180': isExpanded(targetCase?.uuid || '') }"
                      />
                    </button>
                  </TableCell>
                </TableRow>
                <TableRow
                  v-if="isExpanded(targetCase?.uuid || '')"
                  class="border-b border-neutral-100 bg-gray-50 hover:bg-gray-50"
                >
                  <TableCell
                    class="p-3"
                    :colspan="mode === 'manual' ? 5 : 6"
                  >
                    <div class="mb-6 flex gap-6">
                      <!-- Target Case Client Information -->
                      <div class="flex-1">
                        <h3 class="mb-2 text-sm font-medium text-neutral-900">
                          {{ t('clientInformation') }}
                        </h3>
                        <div class="rounded-lg bg-gray-100 p-4">
                          <div class="flex flex-col gap-4">
                            <div
                              v-for="(row, rowIndex) in getInfoFieldsRows(targetCaseClient)"
                              :key="rowIndex"
                              class="grid grid-cols-2 gap-4"
                            >
                              <div
                                v-for="field in row"
                                :key="field.label"
                                class="flex gap-2 text-sm"
                              >
                                <span class="font-medium text-neutral-900">
                                  {{ field.label }}
                                </span>
                                <span class="text-neutral-700">{{ field.value }}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <!-- Target Case Opponent Information -->
                      <div class="flex-1">
                        <h3 class="mb-2 text-sm font-medium text-neutral-900">
                          {{ t('opponentInformation') }}
                        </h3>
                        <div class="rounded-lg bg-gray-100 p-4">
                          <div class="flex flex-col gap-4">
                            <div
                              v-for="(row, rowIndex) in getInfoFieldsRows(targetCaseOpponent)"
                              :key="rowIndex"
                              class="grid grid-cols-2 gap-4"
                            >
                              <div
                                v-for="field in row"
                                :key="field.label"
                                class="flex gap-2 text-sm"
                              >
                                <span class="font-medium text-neutral-900">
                                  {{ field.label }}
                                </span>
                                <span class="text-neutral-700">{{ field.value }}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Case Summary -->
                    <div>
                      <div class="mb-2 flex items-center gap-2">
                        <h3 class="flex items-center gap-2 text-sm font-medium text-neutral-900">
                          {{ t('caseSummary') }}
                        </h3>
                        <span
                          class="flex h-5 w-5 items-center justify-center rounded-sm bg-purple-200 p-1 text-xs font-semibold
                            text-purple-700"
                        >
                          {{ t('ai') }}
                        </span>
                      </div>
                      <div class="rounded-lg bg-gray-100 p-4">
                        <p class="text-sm text-neutral-700">{{ targetCase?.note || '' }}</p>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <!-- Duplicate Cases Section -->
            <p class="mb-4 mt-6 text-base text-neutral-700">
              {{ mode === 'manual' ? t('manualSelectTarget') : t('selectCaseToRemain') }}
            </p>

            <div v-if="mode === 'manual'">
              <div class="mb-3 grid grid-cols-2 gap-2 md:gap-3 lg:grid-cols-5 lg:gap-4">
                <button
                  v-for="card in stateCards"
                  :key="card.value"
                  type="button"
                  :class="[
                    `flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-left transition-all sm:px-5
                    sm:py-3`,
                    stateFilter === card.value
                      ? 'border-purple-600 bg-purple-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  ]"
                  @click="setStateFilter(card.value)"
                >
                  <component
                    :is="card.icon"
                    class="h-5 w-5 flex-shrink-0 text-gray-400"
                  />
                  <span class="text-sm text-gray-600">{{ t(card.labelKey) }}</span>
                </button>
              </div>
              <input
                v-model="searchQuery"
                type="text"
                class="mb-4 w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
                :placeholder="t('searchPlaceholder')"
                @input="onFilterChange"
              />
              <div
                v-if="pageSelectionState === 'all' && totalMatching > selectableOnPage.length"
                class="mb-3 flex items-center justify-between rounded-md bg-purple-50 px-3 py-2 text-sm text-purple-800"
              >
                <span v-if="allMatchingChosen">
                  {{ t('allMatchingSelected', { count: totalMatching }) }}
                </span>
                <button
                  v-else
                  type="button"
                  class="font-medium underline-offset-2 hover:underline"
                  @click="selectAllMatching"
                >
                  {{ t('selectAllMatching', { count: totalMatching }) }}
                </button>
                <button
                  type="button"
                  class="font-medium underline-offset-2 hover:underline"
                  @click="clearSelection"
                >
                  {{ t('clearSelection') }}
                </button>
              </div>
            </div>
            <PaginatedTableView
              v-if="mode === 'manual'"
              ref="tableRef"
              :fetch-data="fetchManualCandidates"
              :show-column-dividers="false"
            >
              <template #table-head>
                <TableHead class="w-[52px]">
                  <input
                    type="checkbox"
                    class="h-5 w-5"
                    :checked="pageSelectionState === 'all'"
                    :indeterminate.prop="pageSelectionState === 'some'"
                    :disabled="selectableOnPage.length === 0"
                    :aria-label="t('selectAllMatching', { count: totalMatching })"
                    @click.stop="togglePageSelection"
                  />
                </TableHead>
                <TableHead class="whitespace-nowrap text-sm font-semibold text-neutral-800">
                  {{ t('name') }}
                </TableHead>
                <TableHead class="whitespace-nowrap text-sm font-semibold text-neutral-800">
                  {{ t('received') }}
                </TableHead>
                <TableHead class="whitespace-nowrap text-sm font-semibold text-neutral-800">
                  {{ t('lawFieldTopic') }}
                </TableHead>
                <TableHead class="whitespace-nowrap text-sm font-semibold text-neutral-800">
                  {{ t('note') }}
                </TableHead>
                <TableHead class="w-[50px]"></TableHead>
              </template>
              <template #table-row="{ items }">
                <template
                  v-for="caseItem in items"
                  :key="caseItem.uuid"
                >
                  <TableRow
                    class="border-b border-neutral-100 hover:bg-neutral-50/50"
                    :class="{
                      'bg-gray-50 hover:bg-gray-50': isExpanded(caseItem.target_case_uuid),
                      'opacity-50': caseItem.target_case_uuid === targetCaseUuid
                    }"
                  >
                    <TableCell
                      class="py-3"
                      @click.stop
                    >
                      <input
                        type="checkbox"
                        class="h-5 w-5"
                        :checked="isCheckboxRowSelected(caseItem.target_case_uuid)"
                        :disabled="caseItem.target_case_uuid === targetCaseUuid"
                        @click.stop="
                          caseItem.target_case_uuid === targetCaseUuid
                            ? null
                            : toggleCheckboxRow(caseItem.target_case_uuid)
                        "
                      />
                    </TableCell>
                    <TableCell class="py-3 text-sm font-medium text-neutral-900">
                      <div class="flex flex-col gap-0.5">
                        <div class="flex items-center gap-2">
                          <span
                            v-if="caseItem.target_case_uuid === targetCaseUuid"
                            class="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700"
                          >
                            {{ t('targetBadge') }}
                          </span>
                          <span>{{ (caseItem as any).case_title || '-' }}</span>
                        </div>
                        <button
                          v-if="caseItem.target_case_uuid !== targetCaseUuid"
                          type="button"
                          class="self-start text-xs font-normal text-purple-700 underline-offset-2 hover:underline"
                          @click.stop="setTargetCase(caseItem.target_case_uuid)"
                        >
                          {{ t('changeTargetCta') }}
                        </button>
                      </div>
                    </TableCell>
                    <TableCell class="whitespace-nowrap py-3 text-sm text-neutral-700">
                      {{ formatDate(caseItem.target_case_created_at) }}
                    </TableCell>
                    <TableCell class="py-1.5 text-sm">
                      <div class="text-neutral-800">
                        {{
                          formatLawFieldTopic(
                            caseItem.target_case_law_field,
                            caseItem.target_case_law_topic
                          )
                        }}
                      </div>
                    </TableCell>
                    <TableCell class="max-w-[200px] py-2">
                      <div class="line-clamp-2 text-sm text-neutral-800">
                        {{ caseItem.target_case_note }}
                      </div>
                    </TableCell>
                    <TableCell class="w-[50px]">
                      <button
                        class="flex h-full w-full items-center justify-center text-neutral-500 hover:text-neutral-900"
                        @click="toggleExpanded(caseItem.target_case_uuid)"
                      >
                        <ChevronDown
                          class="h-6 w-6 transition-transform"
                          :class="{ 'rotate-180': isExpanded(caseItem.target_case_uuid) }"
                        />
                      </button>
                    </TableCell>
                  </TableRow>

                  <TableRow
                    v-if="isExpanded(caseItem.target_case_uuid)"
                    class="border-b border-neutral-100 bg-gray-50 hover:bg-gray-50"
                  >
                    <TableCell
                      class="p-3"
                      colspan="6"
                    >
                      <div
                        v-if="!expandedCaseDetails[caseItem.target_case_uuid]"
                        class="flex gap-6"
                      >
                        <div
                          v-for="panel in 2"
                          :key="panel"
                          class="flex-1"
                        >
                          <SkeletonLoader
                            class="mb-2"
                            width="160px"
                            height="20px"
                          />
                          <div class="rounded-lg bg-gray-100 p-4">
                            <div class="flex flex-col gap-3">
                              <SkeletonLoader
                                v-for="i in 4"
                                :key="i"
                                height="14px"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        v-else
                        class="flex gap-6"
                      >
                        <div class="flex-1">
                          <h3 class="mb-2 text-sm font-medium text-neutral-900">
                            {{ t('clientInformation') }}
                          </h3>
                          <div class="rounded-lg bg-gray-100 p-4">
                            <div class="flex flex-col gap-4">
                              <div
                                v-for="(row, rowIndex) in getInfoFieldsRows(
                                  expandedCaseDetails[caseItem.target_case_uuid].data?.client
                                )"
                                :key="rowIndex"
                                class="grid grid-cols-2 gap-4"
                              >
                                <div
                                  v-for="field in row"
                                  :key="field.label"
                                  class="flex gap-2 text-sm"
                                >
                                  <span class="font-medium text-neutral-900">
                                    {{ field.label }}
                                  </span>
                                  <span class="text-neutral-700">{{ field.value }}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div class="flex-1">
                          <h3 class="mb-2 text-sm font-medium text-neutral-900">
                            {{ t('opponentInformation') }}
                          </h3>
                          <div class="rounded-lg bg-gray-100 p-4">
                            <div class="flex flex-col gap-4">
                              <div
                                v-for="(row, rowIndex) in getInfoFieldsRows(
                                  expandedCaseDetails[caseItem.target_case_uuid].data?.opponent
                                )"
                                :key="rowIndex"
                                class="grid grid-cols-2 gap-4"
                              >
                                <div
                                  v-for="field in row"
                                  :key="field.label"
                                  class="flex gap-2 text-sm"
                                >
                                  <span class="font-medium text-neutral-900">
                                    {{ field.label }}
                                  </span>
                                  <span class="text-neutral-700">{{ field.value }}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                </template>
              </template>
              <template #empty>
                <p class="text-sm text-neutral-500">{{ t('noCasesFound') }}</p>
              </template>
            </PaginatedTableView>

            <RadioGroup
              v-else
              v-model="selectedCaseUuid"
            >
              <Table>
                <TableHeader>
                  <TableRow class="border-b border-neutral-200 bg-neutral-50/50">
                    <TableHead class="w-[52px]"></TableHead>
                    <TableHead class="whitespace-nowrap text-sm font-semibold text-neutral-800">
                      {{ t('clientName') }}
                    </TableHead>
                    <TableHead class="whitespace-nowrap text-sm font-semibold text-neutral-800">
                      {{ t('opponentName') }}
                    </TableHead>
                    <SortableTableHeader
                      field="received_date"
                      :current-sort="sortBy"
                      class="text-sm font-semibold text-neutral-800"
                      @sort="handleSort"
                    >
                      {{ t('received') }}
                    </SortableTableHeader>
                    <TableHead class="whitespace-nowrap text-sm font-semibold text-neutral-800">
                      {{ t('lawFieldTopic') }}
                    </TableHead>
                    <TableHead class="whitespace-nowrap text-sm font-semibold text-neutral-800">
                      {{ t('note') }}
                    </TableHead>
                    <TableHead class="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <template
                    v-for="caseItem in sortedCases"
                    :key="caseItem.uuid"
                  >
                    <TableRow
                      class="border-b border-neutral-100 hover:bg-neutral-50/50"
                      :class="{
                        'bg-gray-50 hover:bg-gray-50': isExpanded(caseItem.target_case_uuid)
                      }"
                    >
                      <TableCell
                        class="py-3"
                        @click.stop
                      >
                        <RadioGroupItem
                          :value="caseItem.target_case_uuid"
                          class="h-5 w-5"
                        />
                      </TableCell>

                      <TableCell class="py-3 text-sm font-medium text-neutral-900">
                        {{ formatEntityName(caseItem.target_case_client) }}
                      </TableCell>

                      <TableCell class="py-3 text-sm text-neutral-700">
                        {{ formatEntityName(caseItem.target_case_opponent) }}
                      </TableCell>

                      <TableCell class="whitespace-nowrap py-3 text-sm text-neutral-700">
                        {{ formatDate(caseItem.target_case_created_at) }}
                      </TableCell>

                      <TableCell class="py-1.5 text-sm">
                        <div class="text-neutral-800">
                          {{
                            formatLawFieldTopic(
                              caseItem.target_case_law_field,
                              caseItem.target_case_law_topic
                            )
                          }}
                        </div>
                      </TableCell>

                      <TableCell class="max-w-[200px] py-2">
                        <div class="line-clamp-2 text-sm text-neutral-800">
                          {{ caseItem.target_case_note }}
                        </div>
                      </TableCell>

                      <TableCell class="w-[50px]">
                        <button
                          class="flex h-full w-full items-center justify-center text-neutral-500 hover:text-neutral-900"
                          @click="toggleExpanded(caseItem.target_case_uuid)"
                        >
                          <ChevronDown
                            class="h-6 w-6 transition-transform"
                            :class="{ 'rotate-180': isExpanded(caseItem.target_case_uuid) }"
                          />
                        </button>
                      </TableCell>
                    </TableRow>

                    <TableRow
                      v-if="isExpanded(caseItem.target_case_uuid)"
                      class="border-b border-neutral-100 bg-gray-50 hover:bg-gray-50"
                    >
                      <TableCell
                        class="p-3"
                        colspan="7"
                      >
                        <div class="mb-6 flex gap-6">
                          <div class="flex-1">
                            <h3 class="mb-2 text-sm font-medium text-neutral-900">
                              {{ t('clientInformation') }}
                            </h3>
                            <div class="rounded-lg bg-gray-100 p-4">
                              <div class="flex flex-col gap-4">
                                <div
                                  v-for="(row, rowIndex) in getInfoFieldsRows(
                                    caseItem.target_case_client
                                  )"
                                  :key="rowIndex"
                                  class="grid grid-cols-2 gap-4"
                                >
                                  <div
                                    v-for="field in row"
                                    :key="field.label"
                                    class="flex gap-2 text-sm"
                                  >
                                    <span class="font-medium text-neutral-900">
                                      {{ field.label }}
                                    </span>
                                    <span class="text-neutral-700">{{ field.value }}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div class="flex-1">
                            <h3 class="mb-2 text-sm font-medium text-neutral-900">
                              {{ t('opponentInformation') }}
                            </h3>
                            <div class="rounded-lg bg-gray-100 p-4">
                              <div class="flex flex-col gap-4">
                                <div
                                  v-for="(row, rowIndex) in getInfoFieldsRows(
                                    caseItem.target_case_opponent
                                  )"
                                  :key="rowIndex"
                                  class="grid grid-cols-2 gap-4"
                                >
                                  <div
                                    v-for="field in row"
                                    :key="field.label"
                                    class="flex gap-2 text-sm"
                                  >
                                    <span class="font-medium text-neutral-900">
                                      {{ field.label }}
                                    </span>
                                    <span class="text-neutral-700">{{ field.value }}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div>
                          <div class="mb-2 flex items-center gap-2">
                            <h3
                              class="flex items-center gap-2 text-sm font-medium text-neutral-900"
                            >
                              {{ t('caseSummary') }}
                            </h3>
                            <span
                              class="flex h-5 w-5 items-center justify-center rounded-sm bg-purple-200 p-1 text-xs font-semibold
                                text-purple-700"
                            >
                              {{ t('ai') }}
                            </span>
                          </div>
                          <div class="rounded-lg bg-gray-100 p-4">
                            <p class="text-sm text-neutral-700">{{ caseItem.reasoning }}</p>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  </template>
                </TableBody>
              </Table>
            </RadioGroup>
          </div>
        </div>

        <div
          v-if="!isLoading && (mode === 'manual' || duplicateCases.length > 0)"
          class="mt-6 flex justify-end gap-3"
        >
          <Button
            variant="secondary"
            @click="handleCancel"
          >
            {{ t('cancel') }}
          </Button>

          <AlertDialog>
            <AlertDialogTrigger as-child>
              <Button :disabled="isButtonDisabled">
                {{ t('mergeAndUpdate') }}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {{ mode === 'manual' ? t('manualMergeDialogTitle') : t('mergeDialogTitle') }}
                </AlertDialogTitle>
                <AlertDialogDescription class="text-sm font-normal text-neutral-800">
                  {{
                    mode === 'manual'
                      ? t('manualMergeDialogDescription')
                      : t('mergeDialogDescription')
                  }}
                </AlertDialogDescription>
                <ul
                  v-if="mode !== 'manual'"
                  class="list-disc pl-5 text-sm font-normal text-neutral-800"
                >
                  <li>{{ t('mergeDialogNote') }}</li>
                </ul>
              </AlertDialogHeader>
              <AlertDialogFooter class="flex gap-4">
                <AlertDialogCancel class="flex-1">{{ t('cancel') }}</AlertDialogCancel>
                <AlertDialogAction
                  class="flex-1 bg-red-600 text-white hover:bg-red-700"
                  @click="handleMerge"
                >
                  {{ mode === 'manual' ? t('manualMergeCta') : t('mergeCases') }}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>

    <AlertDialog :open="isMerging">
      <AlertDialogContent
        :on-escape-key-down="(e: Event) => e.preventDefault()"
        :on-pointer-down-outside="(e: Event) => e.preventDefault()"
      >
        <AlertDialogHeader>
          <AlertDialogTitle class="flex items-center gap-2">
            <Loader2 class="h-5 w-5 animate-spin text-purple-600" />
            {{ t('mergingTitle') }}
          </AlertDialogTitle>
          <AlertDialogDescription class="text-sm font-normal text-neutral-800">
            {{ t('mergingDescription') }}
          </AlertDialogDescription>
        </AlertDialogHeader>
      </AlertDialogContent>
    </AlertDialog>
  </SpaCompatComponent>
</template>
